import {
  ChatStatusEnum,
  feedbackChangeStatus,
  feedbackCreate,
  feedbackMarkAsRead,
  groupMessagesBySeparators,
  isUnreadMessageFromOtherUserById,
  newMessageReceived,
} from '@/entities/feedbacks'
import {
  type ChatMessageModel,
  type CreteMessageModel,
  type GetApiFeedbacksIdPathParams,
  type GetApiFeedbacksIdQueryParams,
  getApiFeedbacksId,
} from '@/shared/api'
import { createQueryModel } from '@/shared/lib/model-factories'
import { cleanInput } from '@/shared/lib/self-validate-fields'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { createGate } from 'effector-react'
import { and, delay, throttle } from 'patronum'

export const FeedbackChatGate = createGate<{
  chatId: string
  onNewMessageRecieved?: VoidFunction
  onOldMessageRecieved?: VoidFunction
  onOldUnreadMessageRecieved?: VoidFunction
}>()

type FeedbacksIdParams = GetApiFeedbacksIdPathParams &
  GetApiFeedbacksIdQueryParams

const feedbacksQueryModel = createQueryModel({
  handler: ({ params }: { params?: FeedbacksIdParams }) =>
    getApiFeedbacksId({
      id: params?.Id ?? '',
      params: params,
    }),
  pagination: true,
  accumulate: (prevStore: ChatMessageModel[], response) => [
    ...prevStore,
    ...response.messages.items,
  ],
})

export const {
  availableSet: feedbackChatAvailableSet,
  unavailableSet: feedbackChatUnavailableSet,
  filtersInit: feedbackChatFiltersInit,
  $filters: $feedbackChatFilters,
  filtersChanged: feedbackChatFiltersChanged,
  filtersReset: feedbackChatFiltersReset,
  pageChanged: feedbackChatPageChanged,
  pageReset: feedbackChatPageReset,
  $page: $feedbackChatPage,
  query: feedbackChatQuery,
  $search: $feedbackChatSearch,
  searchChanged: feedbackChatSearchChanged,
  $accumulatedData: $feedbackChatAccumulatedData,
} = feedbacksQueryModel

/**
 * Feature: Отслеживание скролла
 */

/**
 * Feature: Инициализация чата при открытии и очистка при закрытии
 */
sample({
  clock: FeedbackChatGate.open,
  target: feedbackChatAvailableSet,
})

sample({
  clock: feedbackChatAvailableSet,
  source: FeedbackChatGate.state,
  fn: ({ chatId }) => {
    return { Id: chatId }
  },
  target: feedbackChatFiltersInit,
})

sample({
  clock: FeedbackChatGate.close,
  target: feedbackChatUnavailableSet,
})

/**
 * Feature: Сброс данных чата при смене чата
 */
sample({
  clock: FeedbackChatGate.state,
  filter: ({ chatId }) => !!chatId,
  target: [feedbackChatUnavailableSet, feedbackChatAvailableSet],
})

/**
 * Feature: Контроль скролла при подгрузке новых сообщений
 */
const $scrollToNewestFunction = createStore<VoidFunction | null>(null)
sample({
  clock: FeedbackChatGate.state,
  fn: ({ onNewMessageRecieved }) => onNewMessageRecieved || null,
  target: $scrollToNewestFunction,
})
const scrollToNewestFx = attach({
  source: $scrollToNewestFunction,
  effect(scrollToFirstUnread) {
    scrollToFirstUnread?.()
  },
})
/**
 * Feature: Контроль скролла при подгрузке старых непрочитанных сообщений
 */
const $scrollToOldestUnreadFunction = createStore<VoidFunction | null>(null)
sample({
  clock: FeedbackChatGate.state,
  fn: ({ onOldUnreadMessageRecieved }) => onOldUnreadMessageRecieved || null,
  target: $scrollToOldestUnreadFunction,
})
const scrollToOldestUnreadFx = attach({
  source: $scrollToOldestUnreadFunction,
  effect(scrollToFirstUnread) {
    scrollToFirstUnread?.()
  },
})

/**
 * Feature: Контроль скролла при подгрузке старых сообщений
 */
const $stillScrollAfterLoadingOlderMessagesFunction =
  createStore<VoidFunction | null>(null)
sample({
  clock: FeedbackChatGate.state,
  fn: ({ onOldMessageRecieved }) => onOldMessageRecieved || null,
  target: $stillScrollAfterLoadingOlderMessagesFunction,
})
const stillScrollAfterLoadingOlderMessagesFx = attach({
  source: $stillScrollAfterLoadingOlderMessagesFunction,
  effect(scrollToFirstUnread) {
    scrollToFirstUnread?.()
  },
})
sample({
  clock: feedbackChatQuery.finished.success,
  target: stillScrollAfterLoadingOlderMessagesFx,
})

/**
 * Feature: Догрузка следующих страниц сообщений в чате при достижении конца списка
 */
export const feedbackChatRequestedNextPage = createEvent()
const feedbackChatNextPage = createEvent()

throttle({
  source: feedbackChatRequestedNextPage,
  timeout: 300,
  target: feedbackChatNextPage,
})

const $isNextPage = feedbackChatQuery.$data.map((data) => {
  if (!data) return false
  const {
    messages: { totalItems, itemsOffset, itemsQuantity },
  } = data
  return totalItems > itemsOffset + itemsQuantity
})

sample({
  clock: feedbackChatNextPage,
  source: $feedbackChatPage,
  filter: and(
    $isNextPage,
    feedbackChatQuery.$pending.map((pending) => !pending),
  ),
  fn: (page) => page + 1,
  target: feedbackChatPageChanged,
})

/**
 * Feature: Догрузка всех непрочитанных сообщений в чате
 */
const $isNeedToLoadMoreUntilFirstUnread = feedbackChatQuery.$data.map(
  (data) => {
    // Если нет данных, то не нужно подгружать
    // Это может быть при открытии чата, когда еще нет сообщений
    if (!data) return false
    const { messages } = data

    // Если нет сообщений, то не нужно подгружать
    if (messages.items.length === 0) return false

    // Если уже достигнут конец списка, то не нужно подгружать
    if (messages.totalItems <= messages.itemsOffset + messages.itemsQuantity)
      return false

    // Если достигли непрочитанного сообщения от собеседника, то не нужно подгружать
    if (
      messages.items.some((item) => !item.isCurrentUserMessage && item.isRead)
    )
      return false

    // Иначе нужно подгружать

    return true
  },
)

sample({
  clock: $feedbackChatAccumulatedData,
  filter: $isNeedToLoadMoreUntilFirstUnread,
  target: feedbackChatNextPage,
})

export const $loadingMore = combine(
  feedbackChatQuery.$pending,
  $feedbackChatAccumulatedData,
  (pending, data) => pending && data.length > 0,
)

const $loadingAllUnread = combine(
  feedbackChatQuery.$pending,
  $isNeedToLoadMoreUntilFirstUnread,
  (pending, loadMore) => pending || loadMore,
)

export const $loadingAllUnreadAfterChangingChat = createStore(true)
  .on(feedbackChatAvailableSet, () => true)
  .on($loadingAllUnread, (prevState, loading) => (loading ? prevState : false))
sample({
  clock: delay($loadingAllUnreadAfterChangingChat, 100),
  filter: (loading: boolean) => !loading,
  target: scrollToOldestUnreadFx,
})

export const $loadingChatInitially = combine(
  {
    loadingAllUnreadAfterChangingChat: $loadingAllUnreadAfterChangingChat,
    queryPending: feedbackChatQuery.$pending,
    queryData: feedbackChatQuery.$data,
    queryError: feedbackChatQuery.$error,
  },
  ({
    loadingAllUnreadAfterChangingChat,
    queryPending,
    queryData,
    queryError,
  }) =>
    (!queryError && !queryData && queryPending) ||
    loadingAllUnreadAfterChangingChat,
)

/**
 * Feature: При переключении чата сохраняем неотправленные данные в поле ввода
 */
type ChatId = string
type InputDataByChatId = {
  id: ChatId
  data: CreteMessageModel
}
const changeFeedbackInputsByChatId = createEvent<InputDataByChatId>()
const resetFeedbackInputsByChatId = createEvent<{ id: ChatId }>()
const $feedbackInputsByChatId = createStore<Record<ChatId, CreteMessageModel>>(
  {},
)
  .on(changeFeedbackInputsByChatId, (state, { id, data }) => ({
    ...state,
    [id]: data,
  }))
  .on(resetFeedbackInputsByChatId, (state, { id }) => {
    const newState = { ...state }
    delete newState[id]
    return newState
  })

export const $currentFeedbackInput = combine(
  FeedbackChatGate.state,
  $feedbackInputsByChatId,
  ({ chatId }, inputs) => ({
    text: inputs[chatId]?.text ?? '',
    attachmentId: inputs[chatId]?.attachmentId ?? null,
  }),
)

export const changeCurrentFeedbackInput = createEvent<CreteMessageModel>()
sample({
  clock: changeCurrentFeedbackInput,
  source: FeedbackChatGate.state,
  fn: ({ chatId }, data) => ({
    id: chatId,
    data,
  }),
  target: changeFeedbackInputsByChatId,
})

export const resetCurrentFeedbackInput = createEvent()
sample({
  clock: resetCurrentFeedbackInput,
  source: FeedbackChatGate.state,
  fn: ({ chatId }) => ({ id: chatId }),
  target: resetFeedbackInputsByChatId,
})

/**
 * Feature: Отправка сообщения в чат
 */
export const feedbackChatMessageSubmit = createEvent()

sample({
  clock: feedbackChatMessageSubmit,
  source: { gate: FeedbackChatGate.state, data: $currentFeedbackInput },
  fn: ({ gate, data }) => {
    const cleanDataText = cleanInput(data.text)
    return {
      id: gate.chatId,
      data: {
        text: cleanDataText,
        attachmentId: data.attachmentId,
      },
    }
  },
  target: feedbackCreate.start,
})

sample({
  clock: feedbackCreate.finished.success,
  fn: ({ params }) => ({ id: params.id }),
  target: resetFeedbackInputsByChatId,
})

/**
 * Feature: Группировка сообщений по разделителям для отображения в чате
 */

// Если пользователь провзаимодействовал с чатом, значит можно отмечать сообщения как прочитанные
const $chatId = FeedbackChatGate.state.map(({ chatId }) => chatId, {
  skipVoid: false,
})
export const chatTouchedByUser = createEvent()
export const $isChatTouchedByUser = createStore(false)
  .on(chatTouchedByUser, () => true)
  .reset($chatId)

export const $messagesWithSeparators = combine(
  {
    messages: $feedbackChatAccumulatedData,
    chatTouchedByUser: $isChatTouchedByUser,
  },
  ({ messages, chatTouchedByUser }) => {
    return groupMessagesBySeparators(messages, {
      withUnread: !chatTouchedByUser,
    })
  },
)

/**
 * Feature: Отметка сообщений как прочитанных
 */
type MessageIndex = number
type MessageId = string

const messagesAddedToReadSet = createEvent<MessageId[]>()
const $readMessageIds = createStore<Set<MessageId>>(new Set()).on(
  messagesAddedToReadSet,
  (state, messageIds) => {
    const newState = new Set(state)
    messageIds.forEach((id) => newState.add(id))
    return newState
  },
)
const updateLastVisibleMessageIndex = createEvent<MessageIndex>()
const $lastVisibleMessageIndex = restore(updateLastVisibleMessageIndex, -1)

sample({
  clock: FeedbackChatGate.state,
  target: [$readMessageIds.reinit, $lastVisibleMessageIndex.reinit],
})

const messageMarkedAsVisible = createEvent<MessageId>()
export const messagesSkippedDuringFastScroll = createEvent<MessageIndex>()
export const chatScrolledToEnd = createEvent()

const messageMarkedAsRead = createEvent<MessageId>()

export const markMessageAsVisibleRequested = createEvent<MessageId>()

const throttledMarkMessageAsVisibleReqRequested = throttle({
  source: markMessageAsVisibleRequested,
  timeout: 300,
})

sample({
  clock: throttledMarkMessageAsVisibleReqRequested,
  target: messageMarkedAsVisible,
})

sample({
  clock: throttledMarkMessageAsVisibleReqRequested,
  source: $readMessageIds,
  filter: (readMessages, messageId) =>
    !!messageId && !readMessages.has(messageId),
  fn: (_, messageId) => messageId,
  target: messageMarkedAsVisible,
})

sample({
  clock: messageMarkedAsVisible,
  source: { readMessages: $readMessageIds, messages: $messagesWithSeparators },
  filter: ({ readMessages, messages }, messageId) =>
    !readMessages.has(messageId) &&
    isUnreadMessageFromOtherUserById(messages, messageId),
  fn: (_, messageId) => {
    return messageId
  },
  target: messageMarkedAsRead,
})

sample({
  clock: messageMarkedAsRead,
  fn: (messageId) => [messageId],
  target: messagesAddedToReadSet,
})

sample({
  clock: messagesAddedToReadSet,
  source: combine({
    gate: FeedbackChatGate.state,
    initialLoad: $loadingAllUnreadAfterChangingChat,
  }),
  filter: ({ initialLoad }, messageIds) =>
    !initialLoad && messageIds.length > 0,
  fn: ({ gate: { chatId } }, messageIds) =>
    messageIds.map((messageId) => ({ id: chatId, messageId })),
  target: createEffect((requests: Array<{ id: string; messageId: string }>) => {
    requests.forEach((request) => {
      feedbackMarkAsRead.start(request)
    })
  }),
})

// Update read status
export const $feedbackChatUnreadMessagesCount = createStore<number>(0)

sample({
  clock: $feedbackChatAccumulatedData,
  filter: $loadingAllUnread.map((v) => !v),
  fn: (messages) =>
    messages.filter((m) => !m.isRead && !m.isCurrentUserMessage).length,
  target: $feedbackChatUnreadMessagesCount,
})

sample({
  source: $feedbackChatAccumulatedData,
  clock: feedbackMarkAsRead.finished.success,
  filter: (messages, { params }) => {
    const message = messages.find((m) => m.id === params.messageId)
    return !!message && !message.isRead
  },
  fn: (messages, { params }) => {
    const targetId = params.messageId
    const targetIndex = messages.findIndex((m) => m.id === targetId)
    if (targetIndex === -1) return messages
    const updated = [...messages]
    for (let i = targetIndex; i < updated.length; i++) {
      const msg = updated[i]
      if (msg.isRead) break
      updated[i] = { ...msg, isRead: true }
    }
    return updated
  },
  target: $feedbackChatAccumulatedData,
})

/**
 * Feature: Отметка статуса обращения
 */
export const feedbackChatOpened = createEvent()
export const feedbackChatClosed = createEvent()
export const $feedbackChatStatus = createStore<ChatStatusEnum | null>(null)
  .on(feedbackChatQuery.finished.success, (_, { result: { status } }) => status)
  .on(feedbackChatOpened, () => ChatStatusEnum.Opened)
  .on(feedbackChangeStatus.finished.success, (prev) =>
    prev === ChatStatusEnum.Opened ? ChatStatusEnum.Closed : prev,
  )
const feedbackChatStatusToggled = createEvent()

export const $isFeedbackChangeStatusPending = feedbackChangeStatus.$pending

sample({
  source: $feedbackChatStatus,
  clock: feedbackChatClosed,
  filter: (feedbackChatStatus) =>
    Boolean(feedbackChatStatus) &&
    feedbackChatStatus !== null &&
    feedbackChatStatus === ChatStatusEnum.Opened,
  target: feedbackChatStatusToggled,
})

sample({
  source: combine({
    gate: FeedbackChatGate.state,
    pending: feedbackChangeStatus.$pending,
  }),
  clock: feedbackChatStatusToggled,
  filter: ({ gate: { chatId }, pending }) => Boolean(chatId) && !pending,
  fn: ({ gate: { chatId } }) => ({ id: chatId }),
  target: feedbackChangeStatus.start,
})

/**
 * Feature: Получение новых сообщений в чате по сокету
 */

const addNewMessages = createEvent<ChatMessageModel[]>()

sample({
  clock: newMessageReceived,
  source: FeedbackChatGate.state,
  filter: ({ chatId }, newMessage) => chatId === newMessage.id,
  fn: (_, newMessage) => newMessage.messages.items,
  target: addNewMessages,
})

sample({
  clock: addNewMessages,
  source: $feedbackChatAccumulatedData,
  fn: (messages, newMessages) => {
    const newMessagesFiltered = newMessages.filter(
      (message) => !messages.some((m) => m.id === message.id),
    )
    if (newMessagesFiltered.length === 0) return messages
    return [...newMessagesFiltered, ...messages]
  },
  target: [$feedbackChatAccumulatedData, scrollToNewestFx],
})

sample({
  clock: addNewMessages,
  filter: (newMessages) =>
    !newMessages.some(
      (message) =>
        message.isCurrentUserMessage && message.text === 'Обращение закрыто',
    ),
  target: feedbackChatOpened,
})
